import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";
import { FailurePanel } from "@/components/FailurePanel";
import type { EventJobItem } from "@/features/events/api/events.api";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { eventTopicLabel } from "../lib/event-topic-label";
import { eventTypeLabel } from "../lib/event-type-label";
import { getSentryTraceUrl } from "../lib/sentry-trace-link";
import { EVENT_DETAIL_ROW_GRID, EventDetailSection } from "./shared/EventDetailSection";
import {
  EMPTY_VALUE,
  EventActorIdentity,
  EventIdentityRow,
  EventTenantIdentity,
} from "./shared/EventIdentityRow";
import { EventJsonSection } from "./shared/EventJsonSection";
import { EventStatusBadge } from "./shared/EventStatusBadge";
import { EventOutcomeBadge } from "./shared/EventOutcomeBadge";
import { adminEventSubjectHref, readAdminEventOutcome, readAdminEventSubject } from "../lib/event-subject";
import { EventTimingSection } from "./shared/EventTimingSection";

interface EventDetailDrawerProps {
  event: EventJobItem | null;
  now: number;
  /** Live PROCESSING duration past this reads as timed out. From the ops settings. */
  stuckAfterMs: number;
  onClose: () => void;
  /** Opens the publish sheet on this event's name and payload. Only passed for operators allowed to publish, so its absence hides the action. */
  onRepublish?: (event: EventJobItem) => void;
}

const COPIED_RESET_MS = 2_000;

function CopyValueButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard({ resetMs: COPIED_RESET_MS });

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className={cn(
        "shrink-0 rounded-sm transition-opacity",
        // Touch gets a permanent full-size target, since a hover reveal would
        // never fire there. A mouse gets a compact button that stays out of the
        // way until the field is hovered or the button is tabbed to.
        "min-h-11 min-w-11",
        "[@media(hover:hover)_and_(pointer:fine)]:size-5",
        "[@media(hover:hover)_and_(pointer:fine)]:min-h-0",
        "[@media(hover:hover)_and_(pointer:fine)]:min-w-0",
        // Dropping the hide rule while copied is what keeps the checkmark on
        // screen after the pointer leaves; an override class would lose to the
        // media variant in the cascade.
        !copied && "[@media(hover:hover)_and_(pointer:fine)]:opacity-0",
        "group-hover/field:opacity-100 focus-visible:opacity-100",
      )}
      onClick={() => void copy(value)}
      aria-label={
        copied
          ? t("admin.events.drawer.copied", "Copied")
          : t("admin.events.drawer.copyField", "Copy {{field}}", { field: label })
      }
    >
      <Icon
        name={copied ? "check" : "copy"}
        size="xs"
        colorVar={copied ? "--success" : undefined}
        aria-hidden
      />
    </Button>
  );
}

function IdentityRow({
  label,
  value,
  mono = false,
  copyable = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
}) {
  const isEmpty = value === EMPTY_VALUE;

  return (
    <div className={cn("group/field", EVENT_DETAIL_ROW_GRID)}>
      <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          // A shared line box keeps the row rhythm even where a mono value sits
          // next to a proportional one.
          "leading-5",
          mono ? "break-all font-mono text-[11px]" : "text-xs",
          isEmpty && "text-muted-foreground",
        )}
      >
        {value}
      </dd>
      <dd>{copyable && !isEmpty ? <CopyValueButton value={value} label={label} /> : null}</dd>
    </div>
  );
}

export function EventDetailDrawer({
  event,
  now,
  stuckAfterMs,
  onClose,
  onRepublish,
}: EventDetailDrawerProps) {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);

  if (!event) return null;

  const traceUrl = getSentryTraceUrl(event.sentryTraceId);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      {/* The first tabbable child is now a copy button, and letting Radix
          autofocus it opens the drawer with a focus ring on an icon. Park focus
          on the panel instead so reading order still starts at the top. */}
      <SheetContent
        ref={contentRef}
        aria-describedby={undefined}
        className="focus:outline-none"
        onOpenAutoFocus={(focusEvent) => {
          focusEvent.preventDefault();
          contentRef.current?.focus();
        }}
      >
        <SheetHeader className="gap-0 pr-12">
          <div className="flex flex-wrap items-center gap-2.5">
            <SheetTitle title={event.eventType}>{eventTypeLabel(t, event.eventType)}</SheetTitle>
            <EventStatusBadge status={event.status} />
            {(() => {
              const outcome = readAdminEventOutcome(event.payload, event.attributes, event.status);
              return outcome ? <EventOutcomeBadge outcome={outcome} /> : null;
            })()}
          </div>
          <div
            className="mt-1 text-[10px] tracking-wide text-muted-foreground"
            title={event.topic}
          >
            {eventTopicLabel(t, event.topic)}
          </div>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-6">
          {/* Keyed by event so switching rows does not inherit the previous
              failure's expanded state. */}
          <FailurePanel
            key={`${event.id}-error`}
            code={event.errorCode}
            message={event.errorMessage}
          />

          <EventDetailSection label={t("admin.events.drawer.identity", "Identity")}>
            <dl className="flex flex-col gap-1.5">
              {(() => {
                const subject = readAdminEventSubject(event.payload, event.attributes);
                const href = subject ? adminEventSubjectHref(subject) : null;
                if (!subject) return null;
                const subjectKindLabel = t(`admin.events.subject.${subject.kind}`, subject.kind);
                return (
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-[11px] text-muted-foreground">
                      {t("admin.events.drawer.subject", "Subject")}
                    </dt>
                    <dd>
                      {href ? (
                        <a href={href} className="text-[11px] text-link hover:underline">
                          {t("admin.events.table.subjectLink", "{{kind}} {{id}}", {
                            kind: subjectKindLabel,
                            id: subject.id,
                          })}
                        </a>
                      ) : (
                        <span className="font-mono text-[11px]">
                          {t("admin.events.table.subjectLink", "{{kind}} {{id}}", {
                            kind: subjectKindLabel,
                            id: subject.id,
                          })}
                        </span>
                      )}
                    </dd>
                  </div>
                );
              })()}
              <IdentityRow
                label={t("admin.events.drawer.correlationId", "Correlation ID")}
                value={event.correlationId}
                mono
                copyable
              />
              <IdentityRow
                label={t("admin.events.drawer.idempotencyKey", "Idempotency key")}
                value={event.idempotencyKey ?? EMPTY_VALUE}
                mono
                copyable
              />
              <EventIdentityRow
                label={t("admin.events.drawer.tenant", "Tenant")}
                value={event.tenantId ?? EMPTY_VALUE}
                copyable
                display={
                  <EventTenantIdentity
                    tenantId={event.tenantId}
                    tenantName={event.tenantName}
                    tenantLogoUrl={event.tenantLogoUrl}
                    tenantBrandColor={event.tenantBrandColor}
                  />
                }
              />
              <EventIdentityRow
                label={t("admin.events.drawer.user", "User")}
                value={event.actorUserId ?? event.actorUserName ?? EMPTY_VALUE}
                copyable={Boolean(event.actorUserId)}
                display={
                  <EventActorIdentity
                    name={event.actorUserName ?? event.actorUserId}
                    photoUrl={event.actorUserPhotoUrl}
                  />
                }
              />
              <IdentityRow
                label={t("admin.events.drawer.consumer", "Consumer")}
                value={event.consumerInstanceId ?? EMPTY_VALUE}
                mono
                copyable
              />
            </dl>

            {traceUrl ? (
              <a
                href={traceUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-link hover:underline"
              >
                {t("admin.events.drawer.sentryTrace", "Open the Sentry trace")}
                <Icon name="external-link" size="xs" aria-hidden />
              </a>
            ) : null}
          </EventDetailSection>

          <EventTimingSection event={event} now={now} stuckAfterMs={stuckAfterMs} />

          {/* Keyed by event so switching rows does not inherit the previous
              payload's expanded state. */}
          <EventJsonSection
            key={`${event.id}-payload`}
            label={t("admin.events.drawer.payload", "Payload")}
            value={event.payload}
          />
          <EventJsonSection
            key={`${event.id}-attributes`}
            label={t("admin.events.drawer.attributes", "Attributes")}
            value={event.attributes}
          />
        </SheetBody>

        <SheetFooter className="justify-between px-6">
          <span className="text-[11px] text-muted-foreground">
            {t("admin.events.drawer.eventId", "Event ID")}{" "}
            <span className="font-mono">{event.id}</span>
          </span>
          <div className="flex items-center gap-2">
            {/* Republish copies the name and payload into the publish sheet
                for review rather than firing it blind — a payload the handler
                rejected will be rejected again unchanged, and a succeeded one
                is usually a starting point rather than the exact thing to
                send. */}
            {onRepublish ? (
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-sm text-xs"
                onClick={() => onRepublish(event)}
              >
                <Icon name="refresh-cw" size="xs" aria-hidden />
                {t("admin.events.drawer.republish", "Republish")}
              </Button>
            ) : null}
            <SheetClose asChild>
              <Button variant="outline" size="sm" className="h-8 rounded-sm text-xs">
                {t("admin.events.drawer.close", "Close")}
              </Button>
            </SheetClose>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
